import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert } from '~/shadcn/components/ui/Alert';
import { Button } from '~/shadcn/components/ui/Button';
import { CheckCircle2, XCircle, ChevronDown, ChevronUp } from 'lucide-react';

const BulkShrinkSummary = ({ results, onClose }) => {
    const [showSuccessDetails, setShowSuccessDetails] = useState(false);
    const [showFailureDetails, setShowFailureDetails] = useState(false);

    const successes = results.data?.success || [];
    const failures = results.data?.failed || [];
    const numSuccesses = successes.length;
    const numFailures = failures.length;

    return (
        <div className="p-6 flex flex-col items-center gap-6">
            <div className="w-full space-y-4">
                {numSuccesses > 0 && (
                    <div className="space-y-2">
                        <Alert variant="default" className="bg-green-50 border-green-200 text-green-500">
                            <CheckCircle2 className="h-4 w-4" />
                            <p className="text-xs text-green-800">
                                <span className="font-semibold">Success:</span> {numSuccesses} Shrink Request
                                {numSuccesses !== 1 ? 's' : ''} created successfully
                            </p>
                        </Alert>
                        <div className="ml-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-green-800 p-0 h-auto hover:bg-transparent hover:text-green-900 flex items-center gap-1"
                                onClick={() => setShowSuccessDetails(!showSuccessDetails)}
                            >
                                {showSuccessDetails ? (
                                    <ChevronUp className="h-3 w-3" />
                                ) : (
                                    <ChevronDown className="h-3 w-3" />
                                )}
                                {showSuccessDetails ? 'Hide Details' : 'Show Details'}
                            </Button>
                            {showSuccessDetails && (
                                <div className="mt-2 text-xs text-green-800">
                                    <p className="font-semibold mb-1">SKUs:</p>
                                    <ul className="list-disc pl-4">
                                        {successes.map((sku) => (
                                            <li key={sku}>{sku}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}
                {numFailures > 0 && (
                    <div className="space-y-2">
                        <Alert variant="default" className="bg-red-50 border-red-200 text-red-500">
                            <XCircle className="h-4 w-4" />
                            <p className="text-xs text-red-800">
                                <span className="font-semibold">Failed:</span> {numFailures} Shrink Request
                                {numFailures !== 1 ? 's' : ''} failed to create
                            </p>
                        </Alert>
                        <div className="ml-4">
                            <Button
                                variant="ghost"
                                size="sm"
                                className="text-xs text-red-800 p-0 h-auto hover:bg-transparent hover:text-red-900 flex items-center gap-1"
                                onClick={() => setShowFailureDetails(!showFailureDetails)}
                            >
                                {showFailureDetails ? (
                                    <ChevronUp className="h-3 w-3" />
                                ) : (
                                    <ChevronDown className="h-3 w-3" />
                                )}
                                {showFailureDetails ? 'Hide Details' : 'Show Details'}
                            </Button>
                            {showFailureDetails && (
                                <div className="mt-2 text-xs text-red-800">
                                    <p className="font-semibold mb-1">Failed SKUs:</p>
                                    <ul className="list-disc pl-4 space-y-2">
                                        {failures.map((sku) => (
                                            <li key={sku}>
                                                <span className="font-medium">{sku}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
            <Button variant="default" onClick={onClose}>
                Close
            </Button>
        </div>
    );
};

BulkShrinkSummary.propTypes = {
    results: PropTypes.shape({
        data: PropTypes.shape({
            success: PropTypes.arrayOf(PropTypes.string),
            failed: PropTypes.arrayOf(PropTypes.string),
        }),
    }).isRequired,
    onClose: PropTypes.func.isRequired,
};

export { BulkShrinkSummary };
